import { useEffect, useState } from "react";

import { Box, Button, Heading, Link, Spinner, Text, VStack, useColorModeValue } from "@chakra-ui/react";
import { SignOutButton, useClerk, useUser } from "@clerk/clerk-react";
import { Navigate, Link as RouterLink, useLocation } from "react-router-dom";

import { useAppState } from "~/hooks/useAppState";

type PrivateRouteProps = {
  children: React.ReactNode;
  redirectTo?: string;
};

const RequireAuth = ({ children, redirectTo = "/login" }: PrivateRouteProps) => {
  const clerk = useClerk();
  const user = useUser();
  const location = useLocation();
  const [hasCheckedUserInfo, setHasCheckedUserInfo] = useState(false);
  const { userInfo, setUserInfo, isAuthorized, setIsAuthorized } = useAppState(
    "userInfo",
    "setUserInfo",
    "isAuthorized",
    "setIsAuthorized"
  );

  // Determine if this is the Members page
  const isMembersPage = location.pathname === "/members";

  useEffect(() => {
    if (!clerk.loaded) {
      return;
    }
    if (!user.isSignedIn) {
      setHasCheckedUserInfo(true);
      return;
    }

    if (user.isSignedIn && userInfo) {
      setHasCheckedUserInfo(true);

      console.log(user.user?.organizationMemberships);

      const firstOrganization = user.user?.organizationMemberships?.length
        ? user.user?.organizationMemberships[0].organization
        : null;
      console.log("firstOrganization", firstOrganization);
      console.log("user:", user.user);
      if (firstOrganization) {
        clerk.setActive({ organization: firstOrganization });
      }
    }

    if (user.isSignedIn && !userInfo) {
      const fetchUserInfo = async () => {
        const response = await fetch(`/api/me`);

        if (response.status === 200) {
          const data = await response.json();
          setUserInfo(data);
          console.log("User info stored in global state:", data);
          setHasCheckedUserInfo(true);
          setIsAuthorized(true);
        } else if (response.status === 403) {
          setUserInfo(undefined);
          setHasCheckedUserInfo(true);
          setIsAuthorized(false);
        }
      };
      fetchUserInfo();
    }
  }, [clerk.loaded, user.isSignedIn, userInfo]);

  const isAuthenticated = user.isSignedIn;

  if (!hasCheckedUserInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (isAuthenticated && !isAuthorized && hasCheckedUserInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" width="100vw">
        <VStack>
          <Heading as="h1">Unauthorized</Heading>
          <Heading as="h2" size="sm">
            Sorry, {user.user?.primaryEmailAddress?.emailAddress} is not authorized to view this page
          </Heading>
          <Button variant="secondary" size="sm">
            <SignOutButton />
          </Button>
        </VStack>
      </Box>
    );
  }

  // If this is the Members page and user is not authenticated, show a custom prompt
  if (!isAuthenticated && isMembersPage) {
    const bgColor = useColorModeValue("gray.50", "gray.900");
    const promptBg = useColorModeValue("blue.50", "blue.900");
    const promptBorder = useColorModeValue("blue.200", "blue.700");

    return (
      <Box bg={bgColor} minH="100vh" py={5} px={4}>
        <Box
          maxW="container.xl"
          mx="auto"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minH="60vh"
        >
          <Box
            textAlign="center"
            p={8}
            borderRadius="lg"
            bg={promptBg}
            borderWidth="1px"
            borderColor={promptBorder}
            maxW="md"
            width="100%"
          >
            <Heading size="lg" mb={4}>
              Manage Your Members
            </Heading>
            <Text fontSize="md" mb={6}>
              Please log in or sign up to manage your members, view their health data, and track their progress.
            </Text>
            <Button
              as={RouterLink}
              to={`/signin?redirectTo=${location.pathname}`}
              colorScheme="blue"
              size="lg"
              width="100%"
              mb={4}
            >
              Log In
            </Button>
            <Text fontSize="sm">
              Don't have an account?{" "}
              <Link as={RouterLink} to={`/signup?redirectTo=${location.pathname}`} color="blue.500" fontWeight="bold">
                Sign Up
              </Link>
            </Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return isAuthenticated ? (children as React.ReactElement) : <Navigate to={redirectTo} />;
};

export default RequireAuth;
